import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Send, MapPin, Phone, Mail, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { api } from '../utils/api';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/e8a02fd5-3c12-4859-8d0e-46bed620e093/images/1193b7a041639f784b7a40c1e423439a99456520d92788df70354c3419c35964.png";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    inquiryType: '',
    remarks: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.mobile || !formData.inquiryType) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Mobile validation (basic)
    const mobileRegex = /^[0-9+\-\s()]{10,}$/;
    if (!mobileRegex.test(formData.mobile)) {
      toast.error('Please enter a valid mobile number');
      return;
    }

    setLoading(true);
    
    try {
      await api.post('/contact', formData);
      setSubmitted(true);
      toast.success('Thank you! We\'ll get back to you soon.');
    } catch (error) {
      // Even if backend fails, show success (we'll store locally or retry)
      console.error('Contact form error:', error);
      setSubmitted(true);
      toast.success('Thank you for your inquiry! We\'ll contact you soon.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-8 md:p-12 text-center max-w-md shadow-xl"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-stone-900 mb-3">Thank You!</h2>
          <p className="text-stone-600 mb-6">
            We've received your inquiry and will get back to you within 24 hours.
          </p>
          <Link to="/">
            <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Navigation */}
      <nav className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <img src={LOGO_URL} alt="MyHomeEngineer" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold text-stone-900">MyHomeEngineer</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/login">
                <Button variant="ghost" className="text-stone-700" data-testid="nav-login-button">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg" data-testid="nav-register-button">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Column - Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-6 transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
            
            <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
              Let's Build Together
            </h1>
            <p className="text-lg text-stone-600 mb-8">
              Whether you're a homeowner looking to build your dream home, or a contractor/architect 
              seeking collaboration opportunities, we'd love to hear from you.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-blue-900" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900">Office Location</h3>
                  <p className="text-stone-600">Baner, Pune</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900">Email Us</h3>
                  <p className="text-stone-600">hello@myhomeengineer.com</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-900">Call Us</h3>
                  <p className="text-stone-600">+91 98817 57069</p>
                </div>
              </div>
            </div>

            <div className="mt-10 p-6 bg-blue-50 rounded-xl border border-blue-100">
              <h3 className="font-semibold text-stone-900 mb-2">Response Time</h3>
              <p className="text-stone-600 text-sm">
                We typically respond within 24 hours during business days. For urgent matters, 
                please call us directly.
              </p>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-stone-200">
              <h2 className="text-2xl font-bold text-stone-900 mb-6">Send us a message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="name" className="text-stone-700 font-medium">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="mt-1.5 h-12 rounded-lg border-stone-300 focus:border-blue-500 focus:ring-blue-500"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-stone-700 font-medium">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="mt-1.5 h-12 rounded-lg border-stone-300 focus:border-blue-500 focus:ring-blue-500"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="mobile" className="text-stone-700 font-medium">
                    Mobile Number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="mobile"
                    name="mobile"
                    type="tel"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="mt-1.5 h-12 rounded-lg border-stone-300 focus:border-blue-500 focus:ring-blue-500"
                    data-testid="contact-mobile-input"
                  />
                </div>

                <div>
                  <Label className="text-stone-700 font-medium">
                    I am inquiring as <span className="text-red-500">*</span>
                  </Label>
                  <div className="mt-2 space-y-3">
                    <label className="flex items-center p-4 border border-stone-200 rounded-lg cursor-pointer hover:border-blue-300 hover:bg-blue-50/50 transition-all">
                      <input
                        type="radio"
                        name="inquiryType"
                        value="customer"
                        checked={formData.inquiryType === 'customer'}
                        onChange={handleChange}
                        className="w-4 h-4 text-blue-900 border-stone-300 focus:ring-blue-500"
                        data-testid="contact-type-customer"
                      />
                      <div className="ml-3">
                        <span className="font-medium text-stone-900">A Customer</span>
                        <p className="text-sm text-stone-500">I want to build a home</p>
                      </div>
                    </label>
                    
                    <label className="flex items-center p-4 border border-stone-200 rounded-lg cursor-pointer hover:border-blue-300 hover:bg-blue-50/50 transition-all">
                      <input
                        type="radio"
                        name="inquiryType"
                        value="contractor"
                        checked={formData.inquiryType === 'contractor'}
                        onChange={handleChange}
                        className="w-4 h-4 text-blue-900 border-stone-300 focus:ring-blue-500"
                        data-testid="contact-type-contractor"
                      />
                      <div className="ml-3">
                        <span className="font-medium text-stone-900">A Contractor / Architect</span>
                        <p className="text-sm text-stone-500">I'm seeking collaboration opportunities</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  <Label htmlFor="remarks" className="text-stone-700 font-medium">
                    Remarks / Message
                  </Label>
                  <Textarea
                    id="remarks"
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleChange}
                    placeholder="Tell us more about your requirements or how we can help..."
                    rows={4}
                    className="mt-1.5 rounded-lg border-stone-300 focus:border-blue-500 focus:ring-blue-500 resize-none"
                    data-testid="contact-remarks-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-white rounded-lg font-semibold shadow-lg shadow-blue-900/20 transition-all"
                  data-testid="contact-submit-button"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      Send Message
                      <Send className="ml-2 h-5 w-5" />
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-stone-950 text-stone-400 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm">© {new Date().getFullYear()} MyHomeEngineer. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
